import * as THREE from 'three';

// ── Angle conversion ────────────────────────────────────────────────────────
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

// ── Scalar math ─────────────────────────────────────────────────────────────

/** Clamp x to [lo, hi]. */
export function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

/** Linear interpolation from a to b by t (unclamped). */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** Hermite smoothstep: clamps t to [0, 1] then applies 3t² - 2t³. */
export function smoothstep(t) {
  t = Math.max(0, Math.min(1, t));
  return t * t * (3 - 2 * t);
}

// ── World-axis constants ────────────────────────────────────────────────────
// Treat as read-only — do not mutate. Pass to setFromAxisAngle, etc.
export const X_AXIS = Object.freeze(new THREE.Vector3(1, 0, 0));
export const Y_AXIS = Object.freeze(new THREE.Vector3(0, 1, 0));
export const Z_AXIS = Object.freeze(new THREE.Vector3(0, 0, 1));

// Lookup by axis name ('x' | 'y' | 'z') for config-driven code.
export const AXIS = Object.freeze({ x: X_AXIS, y: Y_AXIS, z: Z_AXIS });

// ── Random helpers ──────────────────────────────────────────────────────────

/** Random float in [lo, hi]. */
export function rand(lo, hi) {
  return lo + Math.random() * (hi - lo);
}

/** Random float in [-1, 1]. */
export function srand() {
  return Math.random() * 2 - 1;
}

/** Random point on a sphere of `mag` radius — returns `{x, y, z}`. */
export function randSphere(mag) {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  const sp = Math.sin(phi);
  return {
    x: sp * Math.cos(theta) * mag,
    y: sp * Math.sin(theta) * mag,
    z: Math.cos(phi) * mag,
  };
}

/** Box-Muller standard-normal sample (zero-alloc, no arrays). */
export function gaussRand() {
  const u = Math.random() || 1e-15; // avoid log(0)
  const v = Math.random() * Math.PI * 2;
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(v);
}

// ── Scheduling ────────────────────────────────────────────────────────────

/**
 * Run `fn` during browser idle time, falling back to a short timeout where
 * requestIdleCallback is unavailable (e.g. Safari). Used to keep heavy one-shot
 * main-thread work (canvas image decodes) off the frame that requests it, so a
 * tile/splat decode landing as the camera pans doesn't spike the render frame.
 * The `timeout` guarantees the work still runs even if the page never idles.
 */
export function runWhenIdle(fn, timeout = 200) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fn, { timeout });
  } else {
    setTimeout(fn, 1);
  }
}
